import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  BookOpen,
  MoreVertical,
  Edit,
  Trash2,
  Copy,
  Eye,
  ChevronRight,
  Filter
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const TrainingsPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchTrainings();
  }, [statusFilter]);

  const fetchTrainings = async () => {
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const response = await api.get(`/trainings${params}`);
      setTrainings(response.data);
    } catch (error) {
      console.error('Failed to fetch trainings:', error);
      toast.error('Erreur lors du chargement des formations');
    } finally {
      setLoading(false);
    }
  };

  const deleteTraining = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette formation ?')) return;
    
    try {
      await api.delete(`/trainings/${id}`);
      setTrainings(trainings.filter(t => t.id !== id));
      toast.success('Formation supprimée');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const duplicateTraining = async (training) => {
    try {
      const response = await api.post('/trainings', {
        title: `${training.title} (copie)`,
        description: training.description,
        is_sequential: true,
        modules: []
      });
      setTrainings([response.data, ...trainings]);
      toast.success('Formation dupliquée');
    } catch (error) {
      toast.error('Erreur lors de la duplication');
    }
  };

  const filteredTrainings = trainings.filter(t =>
    t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const styles = {
      published: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      ready: 'bg-blue-50 text-blue-700 border-blue-200',
      in_progress: 'bg-amber-50 text-amber-700 border-amber-200',
      draft: 'bg-slate-100 text-slate-600 border-slate-200',
      archived: 'bg-slate-100 text-slate-500 border-slate-200'
    };
    const labels = {
      published: 'Publié',
      ready: 'Prêt',
      in_progress: 'En cours',
      draft: 'Brouillon',
      archived: 'Archivé'
    };
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.draft}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-semibold text-slate-900 font-['Outfit']">
            Formations
          </h1>
          <p className="text-slate-500 mt-1">
            Gérez vos formations et modules
          </p>
        </div>
        <Link to="/trainings/new">
          <Button data-testid="create-training-btn" className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle formation
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="search-trainings-input"
            placeholder="Rechercher une formation..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
            <Filter className="w-4 h-4 mr-2 text-slate-400" />
            <SelectValue placeholder="Filtrer par statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="draft">Brouillon</SelectItem>
            <SelectItem value="in_progress">En cours</SelectItem>
            <SelectItem value="ready">Prêt</SelectItem>
            <SelectItem value="published">Publié</SelectItem>
            <SelectItem value="archived">Archivé</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Trainings Grid */}
      {filteredTrainings.length === 0 ? (
        <Card className="border border-slate-200">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-2">
              {searchQuery ? 'Aucun résultat' : 'Aucune formation'}
            </h3>
            <p className="text-slate-500 mb-6">
              {searchQuery 
                ? 'Essayez avec d\'autres termes de recherche'
                : 'Créez votre première formation pour commencer'}
            </p>
            {!searchQuery && (
              <Link to="/trainings/new">
                <Button data-testid="create-first-training-btn" className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Créer une formation
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTrainings.map((training) => (
            <Card 
              key={training.id} 
              className="border border-slate-200 hover:shadow-md transition-all group"
              data-testid={`training-card-${training.id}`}
            >
              <CardContent className="p-0">
                {/* Thumbnail */}
                <div className="aspect-video bg-slate-100 relative overflow-hidden">
                  {training.thumbnail_url ? (
                    <img 
                      src={training.thumbnail_url} 
                      alt={training.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
                      <BookOpen className="w-12 h-12 text-slate-300" />
                    </div>
                  )}
                  
                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Link to={`/trainings/${training.id}`}>
                      <Button size="sm" variant="secondary">
                        <Edit className="w-4 h-4 mr-1" />
                        Éditer
                      </Button>
                    </Link>
                    <Link to={`/learn/${training.id}`}>
                      <Button size="sm" variant="secondary">
                        <Eye className="w-4 h-4 mr-1" />
                        Aperçu
                      </Button>
                    </Link>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-medium text-slate-900 line-clamp-1">
                      {training.title}
                    </h3>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-1 rounded hover:bg-slate-100 transition-colors">
                          <MoreVertical className="w-4 h-4 text-slate-400" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/trainings/${training.id}`)}>
                          <Edit className="w-4 h-4 mr-2" />
                          Modifier
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => duplicateTraining(training)}>
                          <Copy className="w-4 h-4 mr-2" />
                          Dupliquer
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => deleteTraining(training.id)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Supprimer
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  
                  <p className="text-sm text-slate-500 line-clamp-2 mb-3">
                    {training.description || 'Aucune description'}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-400">
                      {training.module_count} modules • {training.lesson_count} leçons
                    </span>
                    {getStatusBadge(training.status)}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default TrainingsPage;
